import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

export default function ServiceTokensSection({ userId, onMessage }) {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("Reply helper");
  const [scope, setScope] = useState("social:x");
  const [expiresInDays, setExpiresInDays] = useState(180);
  const [createdToken, setCreatedToken] = useState("");

  const load = useCallback(async () => {
    const response = await fetch(`${basePath}/api/v7/admin/access/service-tokens/adpass/${userId}`, {
      credentials: "include",
    });
    const data = await response.json();
    if (!data.ok) throw new Error(data.error || "Failed to load tokens");
    setTokens(data.tokens || []);
  }, [userId]);

  useEffect(() => {
    load().catch((error) => onMessage(error.message));
  }, [load, onMessage]);

  async function generate() {
    const response = await fetch(`${basePath}/api/v7/admin/access/service-tokens/adpass/${userId}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, scopes: [scope], expiresInDays: expiresInDays === "never" ? null : Number(expiresInDays) }),
    });
    const data = await response.json();
    if (!data.ok) {
      onMessage(data.error || "Failed to create token");
      return;
    }
    setCreatedToken(data.token);
    onMessage("Token created. Copy it now. It will not be shown again.");
    await load();
  }

  async function revoke(tokenId) {
    if (!window.confirm(`Revoke ${tokenId}?`)) return;
    const response = await fetch(
      `${basePath}/api/v7/admin/access/service-tokens/${tokenId}/revoke/adpass/${userId}`,
      { method: "POST", credentials: "include" }
    );
    const data = await response.json();
    if (!data.ok) {
      onMessage(data.error || "Failed to revoke");
      return;
    }
    await load();
  }

  return (
    <Box mt={4}>
      <Typography style={{ color: "#fff", fontWeight: 700, marginBottom: 8 }}>Service tokens</Typography>
      <Typography style={{ color: "rgba(255,255,255,0.6)", marginBottom: 16 }}>
        Paste a token into the X or Reddit reply helper options. It can only draft replies. It cannot post.
      </Typography>
      <Paper style={{ background: "#262829", padding: 16, marginBottom: 16 }}>
        <Box display="flex" style={{ gap: 12 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            variant="outlined"
            size="small"
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
          />
          <TextField
            select
            label="Scope"
            value={scope}
            onChange={(event) => setScope(event.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 180 }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
          >
            <MenuItem value="social:x">social:x (X drafting)</MenuItem>
            <MenuItem value="social:reddit">social:reddit (Reddit drafting)</MenuItem>
          </TextField>
          <TextField
            select
            label="Expires"
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(event.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 140 }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
          >
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
            <MenuItem value={180}>180 days</MenuItem>
            <MenuItem value="never">Never</MenuItem>
          </TextField>
          <Button variant="contained" color="primary" onClick={generate}>
            Generate token
          </Button>
        </Box>
        {createdToken ? (
          <Box mt={2}>
            <Typography style={{ color: "#facc15" }}>Copy this token now. You will not see it again.</Typography>
            <TextField fullWidth value={createdToken} variant="outlined" size="small" InputProps={{ style: { color: "#fff" }, readOnly: true }} />
          </Box>
        ) : null}
      </Paper>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Id</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Expires</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {tokens.map((token) => (
            <TableRow key={token.tokenId}>
              <TableCell>{token.name}</TableCell>
              <TableCell>{token.tokenId}</TableCell>
              <TableCell>{token.status}</TableCell>
              <TableCell>{token.expiresAt ? new Date(token.expiresAt).toLocaleDateString() : "never"}</TableCell>
              <TableCell>
                {token.status === "active" ? (
                  <Button size="small" onClick={() => revoke(token.tokenId)}>Revoke</Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

ServiceTokensSection.propTypes = {
  userId: PropTypes.string.isRequired,
  onMessage: PropTypes.func.isRequired,
};
